import { HYDRATION_REWRITE_MONITOR_SCRIPT } from './hydration-rewrite-monitor';
import type { SsrTrace } from './ssr-trace';

/** The deployment values the bootstrap stashes on `window` for the client to read back. */
export interface BootstrapRuntimeEnv {
	readonly cdnUrl: string;
	readonly imgproxyUrl: string;
	readonly analyticsCode: string;
	readonly recaptchaSiteKey: string;
	readonly adyenClientKey: string;
}

/**
 * The trace this document was rendered in, as an inline script.
 *
 * Emitted even when there is no trace, as empty ids. The client rebuilds this text from the values it
 * reads back off `window`, so both cases round-trip byte-identically: real ids come back as themselves,
 * and empty ids come back as empty (`currentDocumentTrace` treats empty as absent, and this writes the
 * same empty strings again). Never made conditional — see {@link bootstrapHeadScripts}.
 */
function initTraceScript(trace: SsrTrace | undefined): string {
	return (
		`window.__TRACE_ID=${JSON.stringify(trace?.traceId ?? '')};` +
		`window.__ROOT_SPAN_ID=${JSON.stringify(trace?.rootSpanId ?? '')};`
	);
}

/**
 * Defines `globalThis.__assetUrl` for Vite's `renderBuiltUrl` runtime form (see vite.config.ts) and
 * stashes the deployment env vars on `window` so the rest of the app (router, image, recaptcha, adyen)
 * resolves them at runtime instead of build time. Must execute before the entry module loads any chunk.
 *
 * Its text has to stay byte-identical between server and client: TanStack's `Script` dedups by
 * comparing the text it renders against the inline scripts already in the document, so any divergence
 * makes the comparison fail and the whole bootstrap is re-injected and re-executed on every hydration.
 * That is why the per-request trace ids get their own script rather than being folded in here.
 */
function initAssetUrlScript(env: BootstrapRuntimeEnv): string {
	return (
		`window.__CDN_URL=${JSON.stringify(env.cdnUrl)};` +
		`window.__IMGPROXY_URL=${JSON.stringify(env.imgproxyUrl)};` +
		`window.__ANALYTICS_CODE=${JSON.stringify(env.analyticsCode)};` +
		`window.__GOOGLE_RECAPTCHA_SITE_KEY=${JSON.stringify(env.recaptchaSiteKey)};` +
		`window.__ADYEN_CLIENT_KEY=${JSON.stringify(env.adyenClientKey)};` +
		`globalThis.__assetUrl=function(f){var b=window.__CDN_URL||'';return (b?b.replace(/\\/$/,'')+'/':'/')+f}`
	);
}

/**
 * The inline scripts the document head bootstraps with, in execution order.
 *
 * **Every entry must stay INLINE (hence `string[]`) and unconditional.** Both properties are what keep
 * the head immune to a hydration mismatch, and the reason is specific to how React hydrates `<head>`:
 *
 * - TanStack renders an inline script as `dangerouslySetInnerHTML` + `suppressHydrationWarning`, so
 *   React neither compares its text (the text check short-circuits on `suppressHydrationWarning`) nor
 *   runs its leftover-children check (`shouldSetTextContent` is true for `dangerouslySetInnerHTML`).
 *   An inline script therefore absorbs any positional shift silently.
 * - A script with a `src` and no children gets neither exemption: `shouldSetTextContent` is false, so
 *   React runs the leftover-children check on it. Pair it with an inline script — which has a text
 *   node — and that text node is an unconsumed child, so React throws #418 `args[]=HTML` against a
 *   `<script>` inside `<head>` and regenerates the tree.
 * - `<head>` itself is a HostSingleton, which React excludes from that check, so leftovers sitting
 *   directly in the head are ignored. Only a shift that lands a text-bearing inline script in a
 *   childless script's position can raise the error.
 *
 * That is exactly what reached production on five builds: something inserted a `<script>` into the
 * head ahead of these (Instagram's in-app WebView in most of the affected sessions, which injects its
 * own JS), every rendered script paired with the wrong DOM element, the three inline ones stayed quiet,
 * and the fourth — the Google Analytics tag, the only one with a `src` and no children — raised #418.
 * Keeping this list inline-only means head injection can no longer produce a hydration error at all;
 * the analytics tag now loads after hydration instead, see `loadGtag`.
 *
 * Unconditional matters for a second, independent reason: the conditions once guarding these entries
 * were read from the `window` stash these very scripts write, so anything stopping one from taking
 * effect flipped a condition and dropped an entry on the client only — the same shift, self-inflicted.
 *
 * Order matters: the trace ids first so they are set before any chunk runs, then the runtime env, then
 * the rewrite recorder — which must stay ahead of the entry module, because that module is `async` and
 * on a slow device runs after a page translator has already rewritten the text. Note the recorder
 * watches `characterData` only, so it cannot witness an injected script; a `replacements=0` report does
 * not mean the head was untouched.
 */
export function bootstrapHeadScripts(env: BootstrapRuntimeEnv, trace: SsrTrace | undefined): readonly string[] {
	return [initTraceScript(trace), initAssetUrlScript(env), HYDRATION_REWRITE_MONITOR_SCRIPT];
}
